import { useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useShallow } from 'zustand/react/shallow';
import { BULK_MAX_ROWS, type ApiErrorParams } from '@designdeck/shared';
import type { CreateBulkJobInput } from '@/api/bulk';
import { useCreateBulkJob } from '@/hooks/useBulkCreation';
import { getApiErrorCode } from '@/lib/api-errors';
import {
  computeStatusCounts,
  hasPendingOrFailedCells,
  isBackEnabled,
  isCountingCell,
  isRowTitleMissing,
  type BulkStatusCounts,
} from '@/lib/bulk/pairing';
import { useAuthStore } from '@/stores/authStore';
import {
  isBulkStepComplete,
  selectActiveProducts,
  useBulkCreationStore,
  type BulkRow,
} from '@/stores/bulkCreationStore';

/**
 * Why the create CTA is locked, in the order the hint names them. The
 * pills never lock (R3); this only gates the request.
 */
export type BulkSubmitBlock =
  | 'referenceIncomplete'
  | 'motifsMissing'
  | 'uploadsPending'
  | 'titleMissing'
  | 'tooManyRows'
  | 'accountNotLinked';

/** A failed start: the API code when the answer carried one. */
export interface BulkSubmitError {
  code: string | undefined;
  params: ApiErrorParams | undefined;
}

export interface BulkSubmit {
  counts: BulkStatusCounts;
  /** First reason the CTA is locked, or null when it may send. */
  block: BulkSubmitBlock | null;
  isSubmitting: boolean;
  error: BulkSubmitError | null;
  submit: () => void;
}

export interface UseBulkSubmitOptions {
  /** Called once the server accepted the run, with the product total. */
  onCreated: (totalProducts: number) => void;
}

/** Codes after which the same key must not be sent again. */
const KEY_BURNING_CODES = new Set(['bulk_queue_failed']);

function toRows(rows: readonly BulkRow[], backEnabled: boolean) {
  return rows.flatMap((row) => {
    const front = isCountingCell(row.front, 'front', backEnabled)
      ? row.front
      : undefined;
    const back = isCountingCell(row.back, 'back', backEnabled)
      ? row.back
      : undefined;
    if (!front?.assetId && !back?.assetId) return [];
    return [
      {
        ...(front?.assetId
          ? { frontAssetId: front.assetId, frontFileName: front.fileName }
          : {}),
        ...(back?.assetId
          ? { backAssetId: back.assetId, backFileName: back.fileName }
          : {}),
        title: row.title,
      },
    ];
  });
}

/** The resolved params of a coded API error (e.g. `{ limit: 3 }`). */
function errorParams(error: unknown): ApiErrorParams | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null || !('params' in data)) {
    return undefined;
  }
  const { params } = data;
  if (typeof params !== 'object' || params === null) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value === null || ['string', 'number', 'boolean'].includes(typeof value)
    )
  ) as ApiErrorParams;
}

/**
 * Step 03 submission (R30, R31, R45, R52, R54, R56): derives the request
 * from the store, decides whether the CTA may send and starts the job.
 *
 * Idempotency key: one UUID per flow instance and content. It is created
 * lazily on the first click and kept for retries of the same content, so a
 * double click or a retry after a lost answer returns the same job. It is
 * replaced when the seller enters step 03 again, when the content changes
 * (reference, products, rows, titles, draft option), and after an answer
 * saying the key belongs to another job — the server answers a known key
 * with its existing job, so a changed run must never reuse one.
 *
 * Lives in the flow page, not in step 03, so the result of a request still
 * lands when the seller clicked a pill meanwhile.
 */
export function useBulkSubmit({ onCreated }: UseBulkSubmitOptions): BulkSubmit {
  const { currentStep, reference, activeProductIds, rows, asDraft } =
    useBulkCreationStore(
      useShallow((state) => ({
        currentStep: state.currentStep,
        reference: state.reference,
        activeProductIds: state.activeProductIds,
        rows: state.rows,
        asDraft: state.asDraft,
      }))
    );
  const user = useAuthStore((state) => state.user);
  const mutation = useCreateBulkJob();

  const activeProducts = useMemo(
    () => selectActiveProducts({ reference, activeProductIds }),
    [reference, activeProductIds]
  );
  const counts = useMemo(
    () => computeStatusCounts(rows, activeProducts),
    [rows, activeProducts]
  );

  const backEnabled = isBackEnabled(activeProducts);
  const payload = useMemo(
    (): Omit<CreateBulkJobInput, 'idempotencyKey'> | null =>
      reference
        ? {
            referenceId: reference.id,
            productIds: activeProducts.map((product) => product.productId),
            rows: toRows(rows, backEnabled),
            asDraft,
          }
        : null,
    [reference, activeProducts, rows, backEnabled, asDraft]
  );
  const signature = useMemo(() => JSON.stringify(payload), [payload]);

  // Unknown user (not loaded yet): leave the decision to the server's 409.
  const accountLinked = user ? Boolean(user.externalDesignId) : true;

  const state = { reference, activeProductIds, rows };
  const titleMissing = rows.some((row) => isRowTitleMissing(row, backEnabled));
  let block: BulkSubmitBlock | null = null;
  if (!isBulkStepComplete('reference', state)) block = 'referenceIncomplete';
  else if (counts.designCount === 0) block = 'motifsMissing';
  else if (hasPendingOrFailedCells(rows)) block = 'uploadsPending';
  else if (titleMissing) block = 'titleMissing';
  else if (rows.length > BULK_MAX_ROWS) block = 'tooManyRows';
  else if (!asDraft && !accountLinked) block = 'accountNotLinked';

  const keyRef = useRef<{ signature: string; key: string } | null>(null);
  const inFlightRef = useRef(false);

  // Entering step 03 starts a new attempt with a fresh key.
  useEffect(() => {
    if (currentStep === 'create') keyRef.current = null;
  }, [currentStep]);

  // A changed run makes an earlier failure stale. Never while a request is
  // out: a reset detaches the observer, and its settle callback would be lost.
  const { reset: resetMutation } = mutation;
  useEffect(() => {
    if (!inFlightRef.current) resetMutation();
  }, [signature, resetMutation]);

  const submit = useCallback(() => {
    if (block !== null || payload === null || inFlightRef.current) return;
    if (keyRef.current?.signature !== signature) {
      keyRef.current = { signature, key: crypto.randomUUID() };
    }
    const totalProducts = counts.totalProducts;
    inFlightRef.current = true;
    mutation.mutate(
      { ...payload, idempotencyKey: keyRef.current.key },
      {
        onSuccess: () => onCreated(totalProducts),
        onError: (error) => {
          const code = getApiErrorCode(error);
          if (code && KEY_BURNING_CODES.has(code)) keyRef.current = null;
        },
        onSettled: () => {
          inFlightRef.current = false;
        },
      }
    );
  }, [block, payload, signature, counts.totalProducts, mutation, onCreated]);

  const error: BulkSubmitError | null = mutation.error
    ? {
        code: getApiErrorCode(mutation.error),
        params: errorParams(mutation.error),
      }
    : null;

  return {
    counts,
    block,
    isSubmitting: mutation.isPending,
    error,
    submit,
  };
}
